// The "what does Unconverted mean?" explainer (002 D9), shared by its two triggers: the Opportunity
// Status pill (All tab only) and the Unconverted filter tab itself.
//
// Both render THIS component, so the copy and the interaction can never drift between them — which is
// what D9's amendment requires. Only the `data-cy` differs, because the E2E suite needs to tell the two
// apart.
//
// Why PopoverHandler and not Tooltip, given this opens on hover: D9 requires the explanation to be
// reachable by TAP *as well as* hover. The design-system Tooltip is a @radix-ui/react-tooltip wrapper —
// hover and keyboard focus only, never touch — so it cannot satisfy the tap half. PopoverHandler covers
// tap and click, and the hover half is added here by driving its open state from pointer enter/leave.
// One component, both interactions.
//
// The trigger renders as a <span>, not a <button>, because the tab call site nests it inside
// Tabs.Tab — a Radix Tabs.Trigger, i.e. a <button> — and a button may not contain interactive content.
// PopoverHandler.Trigger uses Radix `asChild`, so the child element decides the tag, and Radix adds
// aria/onClick but never a `tabIndex`.
//
// That makes keyboard reach a per-call-site decision, which is what `focusable` is for. The status pill
// has no nesting constraint, so it opts in and becomes a real tab stop with Enter/Space. The tab icon
// cannot without re-creating the invalid nesting, so it stays unreachable and is marked aria-hidden
// rather than announced as an image the user can neither reach nor act on — keyboard and screen-reader
// users get this explanation from the pill under the All tab.
//
// PLACEMENT IS THE DS DEFAULT (below the trigger), not to the right, and that is a known gap rather
// than a choice: `PopoverHandler.Content` forwards only `sideOffset` and `align` to Radix and neither
// accepts nor spreads a `side`, and no DS tooltip exposes one either (checked against 2.3.1, the latest
// published version). Closing it needs a one-line `side` passthrough on `PopoverHandler.Content` in
// @forecast-it/design-system — its Tooltip.styled already ships the `data-side='right'` animation, so
// the styling is already there. Deliberately NOT worked around here with a hand-rolled tooltip or a
// direct @radix-ui/react-popover import: staying on the DS component keeps this consistent with
// MissingDatePopover in the same column set, and every other @radix-ui reference in src/ is a
// type-only import that erases at build time rather than a runtime dependency on an undeclared package.
import React from 'react';
import {Icon, PopoverHandler} from '@forecast-it/design-system';
import Styled from 'styled-components';

// Containment boundary. PopoverHandler.Content renders through a React portal, and React propagates
// synthetic events through the REACT tree rather than the DOM tree — so a focus (or click) inside the
// portalled popover bubbles back out to the portal's React parent, which at the tab call site is inside
// Tabs.Tab. Radix Tabs activates a tab on focus, and on mousedown, whenever activationMode is not
// 'manual' (the design system never sets it), so opening the explanation on hover was selecting the
// Unconverted tab with no click at all. Stopping these here keeps every interaction with the explainer
// inside the explainer. UnconvertedHelpInTab.test.tsx pins this against the real components.
const HelpRoot = Styled.span`
	display: inline-flex;
	align-items: center;
`;
const HelpTrigger = Styled.span`
	display: inline-flex;
	align-items: center;
	cursor: pointer;
	color: #71717a;
`;
const HelpCard = Styled.div`
	width: 320px;
	padding: 12px 16px;
	color: #232334;
	font-size: 13px;
	line-height: 18px;
`;

export interface UnconvertedHelpProps {
	/** Localized explanation of what "Unconverted" means. */
	help: string;
	/** Selector for the trigger — differs per call site so E2E can address each one. */
	dataCy: string;
	/**
	 * Short localized name for the trigger, e.g. "What does Unconverted mean?".
	 *
	 * Separate from `help` on purpose: `help` is the explanation and belongs in the popover, not in the
	 * button's accessible name, where a screen reader would announce the whole paragraph on every focus
	 * and then again as the content.
	 */
	triggerLabel: string;
	/**
	 * Make the icon a real keyboard tab stop (Enter/Space opens it).
	 *
	 * Only safe where the trigger is NOT nested inside another button: a <button> may not contain
	 * interactive content, so the Unconverted tab call site must leave this off. Defaults to off for
	 * that reason — opting in is the exception, not the rule.
	 */
	focusable?: boolean;
}

// The trigger needs the affordance's name and selector, not the explanation — that belongs to the
// popover content alone, which is the whole point of splitting the two.
type HoverableTriggerProps = Pick<UnconvertedHelpProps, 'dataCy' | 'focusable' | 'triggerLabel'>;

// Lives inside PopoverHandler so it can reach the popover's open state (usePopoverState is context-based,
// exactly as MissingDatePopover's card uses it) and drive it from hover.
const HoverableTrigger = ({dataCy, focusable, triggerLabel}: HoverableTriggerProps): JSX.Element => {
	const {setOpen} = PopoverHandler.usePopoverState();
	// Radix supplies aria-haspopup / aria-expanded, which are only meaningful on an interactive role and
	// are ignored on role="img". So the focusable form is a button, and the non-focusable form is hidden
	// from assistive tech rather than mislabelled as an image nobody can reach.
	// The accessible name is the affordance, not the explanation: naming the button with the whole
	// paragraph makes a screen reader read it on every focus and again as the popover content.
	// Name and description are separate: the button is NAMED by the short affordance label and
	// DESCRIBED by the popover it reveals, so a screen reader announces "What does Unconverted mean?"
	// and then the explanation, instead of reading the paragraph as the name and again as content.
	// The id resolves only while the popover is mounted; an unresolved aria-describedby is ignored.
	const a11y = focusable
		? {role: 'button', tabIndex: 0, 'aria-label': triggerLabel, 'aria-describedby': `${dataCy}-content`}
		: {'aria-hidden': true};
	return (
		// Click OPENS rather than toggles. Radix's Trigger composes its own onOpenToggle after ours and skips
		// it when ours calls preventDefault, so this replaces the toggle outright. A toggle is wrong here:
		// pointer devices fire mouseenter before click — and touch emulates that same sequence on tap — so
		// hover would open the explanation and the click would immediately close it again, leaving a tap
		// showing nothing. That is precisely the interaction 002 D9 requires to work. Closing stays with
		// mouseleave, Escape, and Radix's own outside-press dismissal.
		<PopoverHandler.Trigger
			onMouseEnter={() => setOpen(true)}
			onMouseLeave={() => setOpen(false)}
			onClick={(event: React.MouseEvent) => {
				event.preventDefault();
				setOpen(true);
			}}
		>
			<HelpTrigger
				data-cy={dataCy}
				{...a11y}
				onKeyDown={(event: React.KeyboardEvent) => {
					if (focusable && (event.key === 'Enter' || event.key === ' ')) {
						// Toggle, not open. Radix advertises aria-expanded on this trigger, so a control that
						// only ever opens contradicts its own state. The open-don't-toggle rule below applies
						// to POINTER activation, where mouseenter has already opened it; keyboard activation
						// has no such preceding event.
						event.preventDefault();
						setOpen(open => !open);
					}
				}}
			>
				<Icon icon="help" size="s" />
			</HelpTrigger>
		</PopoverHandler.Trigger>
	);
};

export const UnconvertedHelp = ({help, dataCy, focusable, triggerLabel}: UnconvertedHelpProps): JSX.Element => (
	<HelpRoot
		onFocus={(event: React.FocusEvent) => event.stopPropagation()}
		// BOTH calls are load-bearing, and they do different jobs (DEFECT-2 / TC021):
		//   stopPropagation stops Radix Tabs' own onMouseDown handler firing through React bubbling;
		//   preventDefault suppresses the browser's DEFAULT ACTION for mousedown — focusing the nearest
		//   focusable ancestor, i.e. the tab <button>. That focus originates AT the button, so no
		//   onFocus stop below it can ever see it, and Radix Tabs activates on focus. stopPropagation
		//   cannot cancel a default action, so without preventDefault a real click still selected the tab
		//   even though a synthetic one did not.
		onMouseDown={(event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();
		}}
		onClick={(event: React.MouseEvent) => event.stopPropagation()}
	>
		<PopoverHandler>
			{/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- PopoverHandler types its children
			    as its own Trigger/Content elements; HoverableTrigger *renders* a Trigger, which that signature
			    cannot express. */}
			{(<HoverableTrigger dataCy={dataCy} focusable={focusable} triggerLabel={triggerLabel} />) as any}
			<PopoverHandler.Content showArrow align="center">
				<HelpCard id={`${dataCy}-content`} data-cy={`${dataCy}-content`}>
					{help}
				</HelpCard>
			</PopoverHandler.Content>
		</PopoverHandler>
	</HelpRoot>
);

export default UnconvertedHelp;
